import { useState } from 'react'
import {
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import type { PolicyDocument, PolicyDocumentAction } from './BenefitPolicyService'
import { RecordDialog } from './RecordDialog'

type DocumentSource = 'link' | 'upload'

interface PolicyDocumentDialogProps {
  // The document being edited; absent means this is an add.
  document?: PolicyDocument
  isActive: boolean
  saving: boolean
  refusal?: string
  onClose: () => void
  onConfirm: (action: PolicyDocumentAction) => void
}

/**
 * Add a plan document, or edit the name and audience visibility of one already attached.
 *
 * Editing cannot repoint a document at a different file or URL. Replacing one is
 * an add and a remove, which the section offers separately.
 */
export const PolicyDocumentDialog = ({
  document,
  isActive,
  saving,
  refusal,
  onClose,
  onConfirm,
}: PolicyDocumentDialogProps) => {
  const [name, setName] = useState(document?.name ?? '')
  const [visibleToMember, setVisibleToMember] = useState(document?.is_visible_to_member ?? false)
  const [visibleToProspect, setVisibleToProspect] = useState(
    document?.is_visible_to_prospect ?? false,
  )
  const [source, setSource] = useState<DocumentSource>('link')
  const [url, setUrl] = useState('')
  const [file, setFile] = useState<File | null>(null)

  const isEdit = document !== undefined
  const target = source === 'upload' ? file : url.trim() || null

  const confirm = () => {
    const values = {
      name: name.trim(),
      is_visible_to_member: visibleToMember,
      is_visible_to_prospect: visibleToProspect,
    }
    if (document) {
      onConfirm({ action: 'update', document: document.id, ...values })
    } else if (target instanceof File) {
      onConfirm({ action: 'add', file: target, ...values })
    } else if (target) {
      onConfirm({ action: 'add', url: target, ...values })
    }
  }

  return (
    <RecordDialog
      title={isEdit ? 'Edit document' : 'Add a plan document'}
      confirmLabel={isEdit ? 'Save' : 'Add document'}
      busy={saving}
      incomplete={name.trim() === '' || (!isEdit && target === null)}
      notice={
        isActive
          ? 'This policy is live. While this document is visible to members or prospects, saving reaches them straight away.'
          : undefined
      }
      refusal={refusal}
      testId="document"
      maxWidth="xs"
      onClose={onClose}
      onConfirm={confirm}
    >
      <TextField
        required
        label="Name"
        value={name}
        onChange={event => setName(event.target.value)}
        helperText="What members see in the list."
        data-testid="document-name"
      />
      {isEdit ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          To point this at a different file, add the new document and remove this one.
        </Typography>
      ) : (
        <>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={source}
            onChange={(_event, next: DocumentSource | null) => next && setSource(next)}
          >
            <ToggleButton value="link" data-testid="document-source-link">
              Link
            </ToggleButton>
            <ToggleButton value="upload" data-testid="document-source-upload">
              Upload
            </ToggleButton>
          </ToggleButtonGroup>
          {source === 'link' ? (
            <TextField
              required
              label="URL"
              value={url}
              onChange={event => setUrl(event.target.value)}
              helperText="Where the document is hosted."
              data-testid="document-url"
            />
          ) : (
            <input
              type="file"
              onChange={event => setFile(event.target.files?.[0] ?? null)}
              data-testid="document-file"
            />
          )}
        </>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={visibleToMember}
            onChange={event => setVisibleToMember(event.target.checked)}
            data-testid="document-visible-to-member"
          />
        }
        label="Visible to members"
      />
      <FormControlLabel
        control={
          <Switch
            checked={visibleToProspect}
            onChange={event => setVisibleToProspect(event.target.checked)}
            data-testid="document-visible-to-prospect"
          />
        }
        label="Visible to prospects"
      />
    </RecordDialog>
  )
}
